// packages/shared/api-client/endpoints/tasks.js
// Task management and tracking API endpoints

import { baseApi } from "../baseApi";

/**
 * Task API endpoints
 * Provides task management, assignment, status tracking, and analytics
 */
const taskApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all tasks with filtering
    getTasks: builder.query({
      query: (params = {}) => ({
        url: '/api/tasks',
        params, // { status?, priority?, assignedTo?, createdBy?, page?, limit? }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Task', id })),
              { type: 'Task', id: 'LIST' },
            ]
          : [{ type: 'Task', id: 'LIST' }],
    }),

    // Get tasks assigned to current user
    getTasksAssignedToMe: builder.query({
      query: () => '/api/tasks/assigned-to-me',
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Task', id })),
              { type: 'Task', id: 'MY_TASKS' },
            ]
          : [{ type: 'Task', id: 'MY_TASKS' }],
    }),

    // Get tasks created/assigned by current user
    getTasksAssignedByMe: builder.query({
      query: () => '/api/tasks/assigned-by-me',
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Task', id })),
              { type: 'Task', id: 'ASSIGNED_BY_ME' },
            ]
          : [{ type: 'Task', id: 'ASSIGNED_BY_ME' }],
    }),

    // Get overdue tasks
    getOverdueTasks: builder.query({
      query: () => '/api/tasks/overdue',
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Task', id })),
              { type: 'Task', id: 'OVERDUE' },
            ]
          : [{ type: 'Task', id: 'OVERDUE' }],
    }),

    // Get task analytics
    getTaskAnalytics: builder.query({
      query: () => '/api/tasks/analytics',
      providesTags: ['Task'],
    }),

    // Get tasks by specific status
    getTasksByStatus: builder.query({
      query: (status) => `/api/tasks/by-status/${status}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Task', id })),
              { type: 'Task', id: 'BY_STATUS' },
            ]
          : [{ type: 'Task', id: 'BY_STATUS' }],
    }),

    // Get specific task by ID
    getTaskById: builder.query({
      query: (id) => `/api/tasks/${id}`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    // Create new task
    createTask: builder.mutation({
      query: (taskData) => ({
        url: '/api/tasks',
        method: 'POST',
        body: taskData, // { title, description, priority, dueDate, assignedTo?, category?, ... }
      }),
      invalidatesTags: [
        { type: 'Task', id: 'LIST' },
        { type: 'Task', id: 'MY_TASKS' },
        { type: 'Task', id: 'ASSIGNED_BY_ME' },
      ],
    }),

    // Update task details
    updateTask: builder.mutation({
      query: ({ id, ...taskData }) => ({
        url: `/api/tasks/${id}`,
        method: 'PUT',
        body: taskData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
        { type: 'Task', id: 'MY_TASKS' },
        { type: 'Task', id: 'ASSIGNED_BY_ME' },
      ],
    }),

    // Delete task
    deleteTask: builder.mutation({
      query: (id) => ({
        url: `/api/tasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Task', id: 'LIST' },
        { type: 'Task', id: 'MY_TASKS' },
        { type: 'Task', id: 'ASSIGNED_BY_ME' },
      ],
    }),

    // Assign or reassign task
    assignTask: builder.mutation({
      query: ({ id, assignedToId }) => ({
        url: `/api/tasks/${id}/assign`,
        method: 'PUT',
        body: { assignedToId }, // Backend expects assignedToId, not assignedTo
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
        { type: 'Task', id: 'MY_TASKS' },
        { type: 'Task', id: 'ASSIGNED_BY_ME' },
      ],
    }),

    // Update task status
    updateTaskStatus: builder.mutation({
      query: ({ id, status, notes }) => ({
        url: `/api/tasks/${id}/status`,
        method: 'PUT',
        body: { status, notes }, // Backend accepts optional notes with status update
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
        { type: 'Task', id: 'MY_TASKS' },
        { type: 'Task', id: 'BY_STATUS' },
      ],
    }),

    // Mark task as complete
    markTaskComplete: builder.mutation({
      query: (id) => ({
        url: `/api/tasks/${id}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
        { type: 'Task', id: 'MY_TASKS' },
        { type: 'Task', id: 'OVERDUE' },
      ],
    }),

    // Add note to task
    addTaskNote: builder.mutation({
      query: ({ id, content, isInternal }) => ({
        url: `/api/tasks/${id}/notes`,
        method: 'POST',
        body: { content, isInternal },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Task', id }],
    }),

    // Get task notes
    getTaskNotes: builder.query({
      query: (id) => `/api/tasks/${id}/notes`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    // Get task status history
    getTaskHistory: builder.query({
      query: (id) => `/api/tasks/${id}/history`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    // Upload task document (get presigned URL)
    uploadTaskDocument: builder.mutation({
      query: ({ taskId, fileName, fileType, fileSize }) => ({
        url: `/api/tasks/${taskId}/documents`,
        method: 'POST',
        body: { fileName, fileType, fileSize },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId },
        { type: 'TaskDocument', id: 'LIST' },
      ],
    }),

    // Get task documents
    getTaskDocuments: builder.query({
      query: (taskId) => `/api/tasks/${taskId}/documents`,
      providesTags: (result, error, taskId) => [
        { type: 'TaskDocument', id: 'LIST' },
        { type: 'Task', id: taskId },
      ],
    }),

    // Download task document (get presigned URL)
    downloadTaskDocument: builder.mutation({
      query: ({ taskId, documentId }) => ({
        url: `/api/tasks/${taskId}/documents/${documentId}/download`,
        method: 'GET',
      }),
    }),

    // Delete task document
    deleteTaskDocument: builder.mutation({
      query: ({ taskId, documentId }) => ({
        url: `/api/tasks/${taskId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId },
        { type: 'TaskDocument', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const useGetTasksQuery = taskApi.useGetTasksQuery;
export const useGetTasksAssignedToMeQuery = taskApi.useGetTasksAssignedToMeQuery;
export const useGetTasksAssignedByMeQuery = taskApi.useGetTasksAssignedByMeQuery;
export const useGetOverdueTasksQuery = taskApi.useGetOverdueTasksQuery;
export const useGetTaskAnalyticsQuery = taskApi.useGetTaskAnalyticsQuery;
export const useGetTasksByStatusQuery = taskApi.useGetTasksByStatusQuery;
export const useGetTaskByIdQuery = taskApi.useGetTaskByIdQuery;
export const useCreateTaskMutation = taskApi.useCreateTaskMutation;
export const useUpdateTaskMutation = taskApi.useUpdateTaskMutation;
export const useDeleteTaskMutation = taskApi.useDeleteTaskMutation;
export const useAssignTaskMutation = taskApi.useAssignTaskMutation;
export const useUpdateTaskStatusMutation = taskApi.useUpdateTaskStatusMutation;
export const useMarkTaskCompleteMutation = taskApi.useMarkTaskCompleteMutation;
export const useAddTaskNoteMutation = taskApi.useAddTaskNoteMutation;
export const useGetTaskNotesQuery = taskApi.useGetTaskNotesQuery;
export const useGetTaskHistoryQuery = taskApi.useGetTaskHistoryQuery;
export const useUploadTaskDocumentMutation = taskApi.useUploadTaskDocumentMutation;
export const useGetTaskDocumentsQuery = taskApi.useGetTaskDocumentsQuery;
export const useDownloadTaskDocumentMutation = taskApi.useDownloadTaskDocumentMutation;
export const useDeleteTaskDocumentMutation = taskApi.useDeleteTaskDocumentMutation;
